// The one thing that keeps a running session recorder off the cabinet.
//
// THE RECORDER CANNOT BE STOPPED. The pinned build exposes no handle at all: it
// starts itself when its script runs and records until the page goes away. This
// site is one client-side router, so a visitor who consented on the landing
// page and then clicked through to the cabinet would carry a live recorder onto
// pages that render a customer's end-user document fields — and those are the
// pages the whole route rule exists for.
//
// So the rule is enforced where it cannot be outrun: at the moment a batch is
// SENT. Loading the script only from an allowed address is the first half and
// is not enough on its own, because "allowed" was true when the script loaded
// and is a different question by the time the frames leave.
//
// Browser-safe and framework-free, and deliberately not part of `boot.ts`: this
// is the guarantee, so it is a unit the suite can drive directly rather than
// something only a browser can answer.

import { replayAllowed } from "./routes.ts";
import { isRecorderEndpoint } from "./tracker.ts";

/** Just enough of the global to install the guard on, so a test can pass one. */
export interface FetchHost {
  fetch: typeof fetch;
}

/**
 * Wraps `fetch` so a recorder POST made from a denied address never leaves.
 *
 * Narrow on purpose: anything that is not the recorder's own endpoint is passed
 * straight through to the original, so nothing else on the page can be affected
 * by it. A dropped batch is answered `204` rather than rejected, because the
 * recorder treats a rejection as a reason to retry and a retry is another
 * chance to send the same frames.
 *
 * A batch that straddles a navigation — frames recorded on a public page and
 * flushed a second later from the cabinet — is dropped whole. That loses a
 * public page's last moments and can never leak the other way, which is the
 * direction this has to fail in.
 */
export function installRecorderGuard(host: FetchHost, currentPath: () => string): () => void {
  const original = host.fetch;
  if (typeof original !== "function") return () => {};
  const guarded = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (isRecorderEndpoint(addressOf(input)) && !replayAllowed(safePath(currentPath))) {
      return Promise.resolve(new Response(null, { status: 204 }));
    }
    return original.call(host, input, init);
  }) as typeof fetch;
  host.fetch = guarded;
  return () => {
    // Only if nothing else wrapped it afterwards: restoring over a later
    // wrapper would remove that one instead.
    if (host.fetch === guarded) host.fetch = original;
  };
}

/** The address of whatever `fetch` was handed, or "" when there is not one. */
export function addressOf(input: unknown): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  if (typeof input === "object" && input !== null && "url" in input) {
    const url = (input as { url: unknown }).url;
    return typeof url === "string" ? url : "";
  }
  return "";
}

/** The current path, or "" when asking for it throws — which `replayAllowed` denies. */
function safePath(currentPath: () => string): string {
  try {
    const path = currentPath();
    return typeof path === "string" ? path : "";
  } catch {
    return "";
  }
}
